import React, { createContext, useContext, useState, useEffect } from 'react';
import { employeeApi, adminApi } from '../utils/supabase';

const EmployeeContext = createContext();

export function EmployeeProvider({ children }) {
    const [employees, setEmployees] = useState([]);
    const [admins, setAdmins] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadEmployees();
    }, []);

   // In your EmployeeContext.js file, update the loadEmployees function:

const loadEmployees = async () => {
  try {
    setLoading(true);
    setError(null);
    console.log('🔄 EmployeeContext: Loading ALL users from database...');
    
    // Get ALL users (admins + employees)
    const data = await adminApi.getAllEmployeesAndAdmins();
    
    console.log('📊 EmployeeContext: All users data:', {
      totalRecords: data.length,
      sampleData: data.slice(0, 5)
    });
    
    // Separate into admins and employees
    const adminsOnly = data.filter(d => d.role === 'admin' || d.isAdmin === true);
    const employeesOnly = data.filter(d => d.role !== 'admin' && d.isAdmin !== true);

    console.log('👑 Admins found:', adminsOnly.length);
    console.log('👤 Employees found:', employeesOnly.length);
    
    // Log admin details for debugging
    if (adminsOnly.length > 0) {
      console.log('🔍 Admin details:', adminsOnly.map(a => ({
        id: a.id,
        name: a.name,
        email: a.email,
        role: a.role,
        source: a.source
      })));
    }

    setEmployees(employeesOnly);
    setAdmins(adminsOnly);

    console.log('✅ EmployeeContext: Successfully loaded and set employee data');
    
  } catch (err) {
    console.error('❌ EmployeeContext: Error loading employees:', err);
    setError(`Failed to load employee data: ${err.message}`);
    setEmployees([]);
  } finally {
    setLoading(false);
  }
};

    const addEmployee = async (employeeData) => {
        try {
            setError(null);
            //console.log('🔄 EmployeeContext: Adding new employee with data:', employeeData);
            
            // Validate required fields
            if (!employeeData.first_name || !employeeData.last_name) {
                throw new Error('First name and last name are required');
            }
            
            if (!employeeData.email) {
                throw new Error('Email is required');
            }
            
            if (!employeeData.department) {
                throw new Error('Department is required');
            }
            
            if (!employeeData.position) {
                throw new Error('Position is required');
            }
            
            const newEmployee = await employeeApi.createEmployee(employeeData);
            
            // Add the new employee to the state
            setEmployees(prev => [newEmployee, ...prev]);
            
            //console.log('✅ EmployeeContext: Employee added successfully:', newEmployee);
            return newEmployee;
        } catch (err) {
            console.error('❌ EmployeeContext: Error adding employee:', err);
            setError(err.message);
            throw err;
        }
    };

    const addAdmin = async (adminData) => {
        try {
            setError(null);
            console.log('🔄 EmployeeContext: Adding new admin with data:', adminData);
            
            // Validate required fields
            if (!adminData.name) {
                throw new Error('Name is required');
            }
            
            if (!adminData.email) {
                throw new Error('Email is required');
            }
            
            if (!adminData.password) {
                throw new Error('Password is required');
            }
            
            const newAdmin = await employeeApi.createAdmin(adminData);
            
            console.log('✅ EmployeeContext: Admin added successfully:', newAdmin);
            return newAdmin;
        } catch (err) {
            console.error('❌ EmployeeContext: Error adding admin:', err);
            setError(err.message);
            throw err;
        }
    };

    const updateEmployee = async (employeeId, updates) => {
        try {
            setError(null);
            const updatedEmployee = await employeeApi.updateEmployee(employeeId, updates);
            setEmployees(prev => 
                prev.map(emp => emp.employee_id === employeeId ? updatedEmployee : emp)
            );
            return updatedEmployee;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const deleteEmployee = async (employeeId) => {
        try {
            setError(null);
            await employeeApi.deleteEmployee(employeeId);
            setEmployees(prev => prev.filter(emp => emp.employee_id !== employeeId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Update an existing admin
    const updateAdmin = async (adminId, updates) => {
        try {
            setError(null);
            console.log('🔄 EmployeeContext: Updating admin:', adminId);
            
            // Get current user's role from localStorage or context
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const currentUserRole = currentUser?.role;
            const currentUserId = currentUser?.id;
            const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUserRole === 'super_admin';
            
            // Check if admin is editing their own account
            const isEditingSelf = currentUserId === adminId;
            
            console.log('🔍 Permission check:', {
                currentUserId,
                adminId,
                isEditingSelf,
                currentUserRole,
                isCurrentUserSuperAdmin,
                is_super_admin: currentUser?.is_super_admin
            });
            
            // Allow if: Super Admin OR editing own account
            if (!isCurrentUserSuperAdmin && !isEditingSelf) {
                console.error('❌ Access denied: Not Super Admin and not editing self');
                throw new Error("You don't have access to edit these accounts.");
            }
            
            const updatedAdmin = await employeeApi.updateAdmin(adminId, updates, currentUserRole, currentUserId, isCurrentUserSuperAdmin);
            
            // Update the admins state
            setAdmins(prev => 
                prev.map(admin => admin.id === adminId ? updatedAdmin : admin)
            );
            
            console.log('✅ EmployeeContext: Admin updated successfully:', updatedAdmin);
            return updatedAdmin;
        } catch (err) {
            console.error('❌ EmployeeContext: Error updating admin:', err);
            setError(err.message);
            throw err;
        }
    };

    // Terminate an admin by setting is_active to false
    const terminateAdmin = async (adminId) => {
        try {
            setError(null);
            console.log('🔄 EmployeeContext: Terminating admin:', adminId);
            
            // Get current user's role from localStorage or context
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const currentUserRole = currentUser?.role;
            const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUserRole === 'super_admin';
            
            console.log('🔍 Termination permission check:', {
                currentUserRole,
                isCurrentUserSuperAdmin,
                adminId
            });
            
            // Check if current user is Super Admin (using boolean field or legacy role)
            if (!isCurrentUserSuperAdmin) {
                console.error('❌ Access denied: Not Super Admin');
                throw new Error("You don't have access to terminate these accounts.");
            }
            
            const updatedAdmin = await employeeApi.terminateAdmin(adminId, currentUserRole, isCurrentUserSuperAdmin);
            
            // Update the admins state to reflect the terminated status
            setAdmins(prev => 
                prev.map(admin => admin.id === adminId ? { 
                    ...admin, 
                    is_active: false,
                    status: 'Terminated'
                } : admin)
            );
            
            console.log('✅ EmployeeContext: Admin terminated successfully:', updatedAdmin);
            return updatedAdmin;
        } catch (err) {
            console.error('❌ EmployeeContext: Error terminating admin:', err);
            setError(err.message);
            throw err;
        }
    };

    // Utility functions for the new data structure
    const getEmployeeFullName = (employee) => {
        if (employee.first_name && employee.last_name) {
            return `${employee.first_name} ${employee.last_name}`.trim();
        }
        return employee.name || 'Unknown Employee';
    };

    const getEmployeesByDepartment = (department) => {
        return employees.filter(emp => emp.department === department);
    };

    const getEmployeesByPosition = (position) => {
        return employees.filter(emp => emp.position === position);
    };

    const getPositionOptions = (department) => {
        const positionMap = {
            'Administration': ['General Manager', 'IT Manager'],
            'Development': ['Front End Developer', 'Back End Developer', 'Application Developer', 'Web Developer', 'AI Developer', 'PLC Programmer', 'Embedded System Engineer'],
            'Design': ['UX/UI Designer'],
            'Interns': ['Front End Developer', 'Back End Developer', 'Application Developer', 'Web Developer', 'AI Developer', 'UX/UI Designer', 'PLC Programmer', 'Embedded System Engineer']
        };
        return positionMap[department] || [];
    };

    // Check if a user is Super Admin using boolean field (with backward compatibility)
    const isSuperAdmin = (user) => {
        return user?.is_super_admin === true || user?.role === 'super_admin';
    };

    // Check if user can edit another user
    const canEditUser = (currentUser, targetUser) => {
        const isCurrentSuperAdmin = currentUser?.is_super_admin === true || currentUser?.role === 'super_admin';
        const isTargetSuperAdmin = targetUser?.is_super_admin === true || targetUser?.role === 'super_admin';
        
        // Super Admin can edit anyone
        if (isCurrentSuperAdmin) return true;
        // Regular admin cannot edit Super Admin
        if (isTargetSuperAdmin) return false;
        // Regular admin can edit employees and other admins
        return true;
    };

    // Check if user can terminate another user
    const canTerminateUser = (currentUser, targetUser) => {
        const isCurrentSuperAdmin = currentUser?.is_super_admin === true || currentUser?.role === 'super_admin';
        const isTargetSuperAdmin = targetUser?.is_super_admin === true || targetUser?.role === 'super_admin';
        
        // Super Admin can terminate anyone except themselves (self-preservation)
        if (isCurrentSuperAdmin) {
            return targetUser?.id !== currentUser?.id;
        }
        // Regular admin cannot terminate Super Admin
        if (isTargetSuperAdmin) return false;
        // Regular admin can terminate employees and other admins
        return true;
    };

    // Check if current user can edit admin accounts
    // Returns true only if current user is Super Admin
    const canEditAdmin = (currentUser) => {
        return currentUser?.is_super_admin === true || currentUser?.role === 'super_admin';
    };

    // Check if current user can edit a specific user
    // Regular admins can only edit employees, not other admins
    const canEditSpecificUser = (currentUser, targetUser) => {
        const isCurrentSuperAdmin = currentUser?.is_super_admin === true || currentUser?.role === 'super_admin';
        const isTargetSuperAdmin = targetUser?.is_super_admin === true || targetUser?.role === 'super_admin';
        const isTargetAdmin = targetUser?.role === 'admin' || targetUser?.role === 'super_admin' || targetUser?.isAdmin === true || targetUser?.is_super_admin === true;
        
        // Super Admin can edit anyone
        if (isCurrentSuperAdmin) return true;
        
        // Regular admin cannot edit any admin accounts (including Super Admin)
        if (isTargetAdmin || isTargetSuperAdmin) return false;
        
        // Regular admin can edit employees
        return true;
    };

    return (
        <EmployeeContext.Provider value={{
            employees,
            admins,
            loading,
            error,
            addEmployee,
            addAdmin,
            updateEmployee,
            updateAdmin,
            deleteEmployee,
            terminateAdmin,
            refreshEmployees: loadEmployees,
            // Utility functions
            getEmployeeFullName,
            getEmployeesByDepartment,
            getEmployeesByPosition,
            getPositionOptions,
            // Permission functions
            isSuperAdmin,
            canEditUser,
            canTerminateUser,
            canEditAdmin,
            canEditSpecificUser
        }}>
            {children}
        </EmployeeContext.Provider>
    );
}

export function useEmployees() {
    const context = useContext(EmployeeContext);
    if (!context) {
        throw new Error('useEmployees must be used within an EmployeeProvider');
    }
    return context;
}